import React, { useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ThermometerIcon, DropletsIcon, ZapIcon, ActivityIcon, SettingsIcon, LightbulbIcon, ToggleLeftIcon, GaugeIcon, WindIcon, CloudIcon, ChevronLeftIcon, ChevronRightIcon, LockIcon, KeyIcon, XIcon, PencilIcon, StarIcon, PlusIcon, CheckIcon, PersonStandingIcon, RadarIcon, HeartPulseIcon, ShieldCheckIcon } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line } from 'recharts';
type SensorType = 'SEO' | 'SEL' | 'SES' | 'RADAR';
type TimeRange = 'realtime' | 'daily' | 'weekly' | 'monthly';
type Mode = 'normal' | 'occupancy' | 'co2' | 'condensation' | 'ai' | 'always';
// SEL Mode types
type SELMode = 'occupancy' | 'normal' | 'ai';
const SEL_MODES: {
  id: SELMode;
  label: string;
}[] = [{
  id: 'occupancy',
  label: '재실'
}, {
  id: 'normal',
  label: '일반'
}, {
  id: 'ai',
  label: 'AI모드'
}];
interface Sensor {
  id: string;
  type: SensorType;
  label: string;
  roomId: string;
  active: boolean;
}
interface SensorDetailSidebarProps {
  sensor: Sensor;
  onBack: () => void;
  onClose: () => void;
}
const MODES: {
  id: Mode;
  label: string;
}[] = [{
  id: 'normal',
  label: '일반'
}, {
  id: 'occupancy',
  label: '재실'
}, {
  id: 'co2',
  label: 'CO2'
}, {
  id: 'condensation',
  label: '결로'
}, {
  id: 'ai',
  label: 'AI선정'
}, {
  id: 'always',
  label: '상시'
}];
const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  realtime: '실시간',
  daily: '일간',
  weekly: '주간',
  monthly: '월간'
};
// Mock Data generators
const genData = (len: number, labels: string[], base: number, variance: number) => labels.map(name => ({
  name,
  value: +(base + Math.random() * variance).toFixed(1)
}));
const MOCK_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 20, 5),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 18, 8),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 20, 10),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 15, 15)
};
// SEL-specific mock data
const SEL_POWER_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 8, 4),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 5, 12),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 1.2, 3),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 0.8, 4)
};
const SEL_PRESSURE_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 1010, 8),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 1008, 12),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 1005, 15),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 1000, 20)
};
// SES-specific mock data
const SES_TVOC_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 120, 80),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 100, 150),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 80, 200),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 90, 180)
};
const SES_CO2_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 400, 200),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 380, 300),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 350, 350),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 360, 320)
};
const SES_DUST_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 15, 20),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 12, 30),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 10, 40),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 8, 45)
};
// Reusable time range tab bar
function TimeRangeTabs({
  value,
  onChange
}: {
  value: TimeRange;
  onChange: (t: TimeRange) => void;
}) {
  return <div className="flex bg-white/5 rounded-lg p-0.5" data-id="element-504">
      {(['realtime', 'daily', 'weekly', 'monthly'] as TimeRange[]).map(t => <button key={t} onClick={() => onChange(t)} className={`
            px-2 py-1 rounded-md text-[0.7rem] uppercase font-bold transition-all
            ${value === t ? 'bg-white/10 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}
          `} data-id="element-505">
          {t.slice(0, 1).toUpperCase()}
        </button>)}
    </div>;
}
// Reusable chart section
function ChartSection({
  icon: Icon,
  iconColor,
  title,
  data,
  timeRange,
  onTimeRangeChange,
  chartType = 'area',
  color,
  gradientId,
  stats,
  className = ''
}: {
  icon: any;
  iconColor: string;
  title: string;
  data: {
    name: string;
    value: number;
  }[];
  timeRange: TimeRange;
  onTimeRangeChange: (t: TimeRange) => void;
  chartType?: 'area' | 'bar' | 'line';
  color: string;
  gradientId: string;
  stats?: {
    label: string;
    value: string;
    color?: string;
  }[];
  className?: string;
}) {
  const [localChartType, setLocalChartType] = useState<'area' | 'bar' | 'line'>(chartType);
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className={`glass-panel rounded-xl p-3 flex flex-col ${className}`} data-id="element-506">
      <div className="flex items-center justify-between mb-2 flex-shrink-0" data-id="element-507">
        <div className="flex items-center gap-2" data-id="element-508">
          <Icon className={`w-3.5 h-3.5 ${iconColor}`} data-id="element-509" />
          <span className="text-[0.9rem] font-bold text-slate-300" data-id="element-510">
            {title}
          </span>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-511">
          {/* Chart type toggle */}
          <div className="flex bg-white/5 rounded-lg p-0.5 mr-1" data-id="element-512">
            <button onClick={() => setLocalChartType('bar')} className={`${`p-1 rounded-md transition-all ${localChartType === 'bar' ? 'bg-white/10 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} pt-[6.5px] pb-[6.5px] pl-[6.5px] pr-[6.5px]`} title="Bar Chart" data-id="element-513">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-514">
                <rect x="3" y="12" width="4" height="9" rx="1" data-id="element-515" />
                <rect x="10" y="6" width="4" height="15" rx="1" data-id="element-516" />
                <rect x="17" y="3" width="4" height="18" rx="1" data-id="element-517" />
              </svg>
            </button>
            <button onClick={() => setLocalChartType('line')} className={`${`p-1 rounded-md transition-all ${localChartType === 'line' ? 'bg-white/10 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} pt-[6.5px] pb-[6.5px] pl-[6.5px] pr-[6.5px]`} title="Line Chart" data-id="element-518">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-519">
                <path d="M3 16 L9 11 L15 14 L21 6" data-id="element-520" />
              </svg>
            </button>
            <button onClick={() => setLocalChartType('area')} className={`${`p-1 rounded-md transition-all ${localChartType === 'area' ? 'bg-white/10 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} pt-[6.5px] pb-[6.5px] pl-[6.5px] pr-[6.5px]`} title="Area Chart" data-id="element-521">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-522">
                <path d="M3 20 L3 8 L9 13 L15 6 L21 12 L21 20 Z" fill="currentColor" opacity="0.2" data-id="element-523" />
                <path d="M3 8 L9 13 L15 6 L21 12" data-id="element-524" />
              </svg>
            </button>
          </div>
          <TimeRangeTabs value={timeRange} onChange={onTimeRangeChange} data-id="element-525" />
        </div>
      </div>

      <div className="flex-1 min-h-0 w-full" data-id="element-526">
        <ResponsiveContainer width="100%" height="100%" data-id="element-527">
          {localChartType === 'bar' ? <BarChart data={data} data-id="element-528">
              <XAxis dataKey="name" hide data-id="element-529" />
              <Tooltip cursor={{
            fill: 'rgba(255,255,255,0.05)'
          }} contentStyle={{
            backgroundColor: '#0a0f1a',
            borderColor: '#334155',
            fontSize: '10px'
          }} itemStyle={{
            color
          }} data-id="element-530" />
              <Bar dataKey="value" fill={color} radius={[2, 2, 0, 0]} opacity={0.8} data-id="element-531" />
            </BarChart> : localChartType === 'line' ? <LineChart data={data} data-id="element-532">
              <XAxis dataKey="name" hide data-id="element-533" />
              <Tooltip contentStyle={{
            backgroundColor: '#0a0f1a',
            borderColor: '#334155',
            fontSize: '10px'
          }} itemStyle={{
            color
          }} data-id="element-534" />
              <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} data-id="element-535" />
            </LineChart> : <AreaChart data={data} data-id="element-536">
              <defs data-id="element-537">
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1" data-id="element-538">
                  <stop offset="5%" stopColor={color} stopOpacity={0.3} data-id="element-539" />
                  <stop offset="95%" stopColor={color} stopOpacity={0} data-id="element-540" />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" hide data-id="element-541" />
              <YAxis hide data-id="element-542" />
              <Tooltip contentStyle={{
            backgroundColor: '#0a0f1a',
            borderColor: '#334155',
            fontSize: '10px'
          }} itemStyle={{
            color
          }} data-id="element-543" />
              <Area type="monotone" dataKey="value" stroke={color} fillOpacity={1} fill={`url(#${gradientId})`} data-id="element-544" />
            </AreaChart>}
        </ResponsiveContainer>
      </div>

      {stats && <div className="flex justify-between mt-1.5 text-xs flex-shrink-0" data-id="element-545">
          {stats.map((s, i) => <div key={i} className="text-center" data-id="element-546">
              <div className="text-slate-500 text-[0.7rem] uppercase" data-id="element-547">
                {s.label}
              </div>
              <div className={`font-mono ${s.color || 'text-white'}`} data-id="element-548">
                {s.value}
              </div>
            </div>)}
        </div>}
    </motion.div>;
}
// ─── Password Toast Component ───
function PasswordToast({
  type,
  onClose,
  onVerify,
  onChangeComplete,
  storedPassword
}: {
  type: 'verify' | 'change';
  onClose: () => void;
  onVerify?: (success: boolean) => void;
  onChangeComplete?: (newPw: string) => void;
  storedPassword: string;
}) {
  const [step, setStep] = useState<'verify' | 'newPw' | 'confirm'>(type === 'verify' ? 'verify' : 'verify');
  const [digits, setDigits] = useState(['', '', '', '']);
  const [newDigits, setNewDigits] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const inputRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];
  const newInputRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];
  const handleDigitChange = (index: number, value: string, refs: React.RefObject<HTMLInputElement>[], setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    setter(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setError('');
    if (value && index < 3) {
      refs[index + 1]?.current?.focus();
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent, refs: React.RefObject<HTMLInputElement>[], currentDigits: string[], setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    if (e.key === 'Backspace' && !currentDigits[index] && index > 0) {
      refs[index - 1]?.current?.focus();
    }
  };
  const renderInputs = (currentDigits: string[], refs: React.RefObject<HTMLInputElement>[], setter: React.Dispatch<React.SetStateAction<string[]>>) => <div className="flex gap-2 justify-center my-4" data-id="element-549">
      {currentDigits.map((d, i) => <input key={i} ref={refs[i]} type="password" inputMode="numeric" maxLength={1} value={d} onChange={e => handleDigitChange(i, e.target.value, refs, setter)} onKeyDown={e => handleKeyDown(i, e, refs, currentDigits, setter)} className="w-10 h-12 rounded-lg bg-white/5 border border-white/10 text-center text-lg font-mono text-white focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/50 transition-all" autoFocus={i === 0} data-id="element-550" />)}
    </div>;
  const handleVerify = () => {
    const entered = digits.join('');
    if (entered.length < 4) {
      setError('4자리를 모두 입력해주세요');
      return;
    }
    if (entered !== storedPassword) {
      setError('비밀번호가 일치하지 않습니다');
      setDigits(['', '', '', '']);
      inputRefs[0]?.current?.focus();
      return;
    }
    if (type === 'verify') {
      onVerify?.(true);
    } else {
      setStep('newPw');
      setError('');
    }
  };
  const handleSetNew = () => {
    const newPw = newDigits.join('');
    if (newPw.length < 4) {
      setError('4자리를 모두 입력해주세요');
      return;
    }
    onChangeComplete?.(newPw);
  };
  const getTitle = () => {
    if (step === 'verify') return type === 'verify' ? '비밀번호 확인' : '기존 비밀번호 확인';
    return '새 비밀번호 설정';
  };
  const getSubtitle = () => {
    if (step === 'verify') return '기존 비밀번호 4자리를 입력하세요';
    return '새로운 비밀번호 4자리를 입력하세요';
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[200] flex items-center justify-center" onClick={onClose} data-id="element-551">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-2xl backdrop-saturate-0 backdrop-brightness-50" data-id="element-552" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} transition={{
      type: 'spring',
      stiffness: 400,
      damping: 30
    }} onClick={e => e.stopPropagation()} className="relative z-10 w-[90%] max-w-sm" data-id="element-553">
        <div className="glass-panel rounded-2xl p-6 border-l-4 border-l-cyan-400 shadow-2xl" data-id="element-554">
          <div className="flex items-center gap-3 mb-2" data-id="element-555">
            <div className="p-2 rounded-lg bg-cyan-500/15 text-cyan-400" data-id="element-556">
              <KeyIcon className="w-5 h-5" data-id="element-557" />
            </div>
            <div data-id="element-558">
              <div className="text-base font-bold text-white" data-id="element-559">{getTitle()}</div>
              <div className="text-[0.65rem] text-slate-500" data-id="element-560">
                {getSubtitle()}
              </div>
            </div>
          </div>

          {step === 'verify' ? renderInputs(digits, inputRefs, setDigits) : renderInputs(newDigits, newInputRefs, setNewDigits)}

          <AnimatePresence data-id="element-561">
            {error && <motion.div initial={{
            opacity: 0,
            y: -5
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} className="text-red-400 text-xs text-center mb-3 font-medium" data-id="element-562">
                {error}
              </motion.div>}
          </AnimatePresence>

          <div className="flex gap-3 justify-end" data-id="element-563">
            <button onClick={onClose} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 text-sm font-medium hover:bg-white/10 transition-all active:scale-95" data-id="element-564">
              <XIcon className="w-3.5 h-3.5" data-id="element-565" />
              취소
            </button>
            <button onClick={step === 'verify' ? handleVerify : handleSetNew} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-sm font-medium hover:bg-cyan-500/30 transition-all active:scale-95" data-id="element-566">
              <LockIcon className="w-3.5 h-3.5" data-id="element-567" />
              {step === 'verify' ? '확인' : '설정'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
// ─── SEL Detail View ───
function SELDetail({
  sensor,
  onBack,
  onClose
}: SensorDetailSidebarProps) {
  const [powerRange, setPowerRange] = useState<TimeRange>('realtime');
  const [tempHumidityRange, setTempHumidityRange] = useState<TimeRange>('realtime');
  const [pressureRange, setPressureRange] = useState<TimeRange>('realtime');
  const [selMode, setSelMode] = useState<SELMode>('occupancy');
  const [isLightOn, setIsLightOn] = useState(true);
  const [isOccupied, setIsOccupied] = useState(true);
  const currentSelModeIndex = SEL_MODES.findIndex(m => m.id === selMode);
  const navigateSelMode = (direction: -1 | 1) => {
    const nextIndex = (currentSelModeIndex + direction + SEL_MODES.length) % SEL_MODES.length;
    setSelMode(SEL_MODES[nextIndex].id);
  };
  const combinedData = MOCK_DATA[tempHumidityRange].map((item, i) => ({
    name: item.name,
    temp: item.value,
    humidity: +(30 + Math.random() * 30).toFixed(1)
  }));
  const stableHumidityData = useMemo(() => {
    return MOCK_DATA[tempHumidityRange].map((item, i) => ({
      name: item.name,
      temp: item.value,
      humidity: +(38 + Math.sin(i * 0.8) * 12 + i % 3 * 2).toFixed(1)
    }));
  }, [tempHumidityRange]);
  return <div className="w-full flex flex-col gap-2 p-3 border-l border-white/5 bg-[#0a0f1a]/30 h-full overflow-hidden" data-id="element-568">
      <SensorHeader sensor={sensor} subtitle="Smart Edge Light" onBack={onBack} onClose={onClose} data-id="element-569" />

      {/* SEL Mode Selection - Spin Box + Light/Presence Icons */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="glass-panel rounded-xl p-2.5 flex-shrink-0" data-id="element-570">
        <div className="flex items-center gap-2" data-id="element-571">
          {/* Spin Box - half width */}
          <div className="flex items-center w-2/3 bg-white/5 rounded-lg border border-white/10 overflow-hidden" data-id="element-572">
            <button onClick={() => navigateSelMode(-1)} className="p-2.5 bg-cyan-500/10 hover:bg-cyan-500/25 transition-all text-cyan-400 hover:text-cyan-300 flex-shrink-0 border-r border-white/10 active:scale-90" data-id="element-573">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-574" />
            </button>
            <div className="flex-1 text-center py-1.5 relative overflow-hidden" data-id="element-575">
              <AnimatePresence mode="wait" data-id="element-576">
                <motion.div key={selMode} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.15
              }} className="text-sm font-bold text-cyan-400" data-id="element-577">
                  {SEL_MODES[currentSelModeIndex]?.label}
                </motion.div>
              </AnimatePresence>
            </div>
            <button onClick={() => navigateSelMode(1)} className="p-2.5 bg-cyan-500/10 hover:bg-cyan-500/25 transition-all text-cyan-400 hover:text-cyan-300 flex-shrink-0 border-l border-white/10 active:scale-90" data-id="element-578">
              <ChevronRightIcon className="w-4 h-4" data-id="element-579" />
            </button>
          </div>

          {/* Light + Presence icons - right aligned */}
          <div className="flex-1 flex items-center justify-end gap-2" data-id="element-580">
            <div className={`flex flex-col items-center gap-1 p-1.5 rounded-xl ${isLightOn ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-slate-600'}`} title="조명 상태" data-id="element-581">
              <LightbulbIcon className="w-7 h-7" fill={isLightOn ? 'currentColor' : 'none'} data-id="element-582" />
            </div>
            <div className={`flex flex-col items-center gap-1 p-1.5 rounded-xl ${isOccupied ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/5 text-slate-600'}`} title="재실 여부" data-id="element-583">
              <PersonStandingIcon className="w-7 h-7" fill={isOccupied ? 'currentColor' : 'none'} data-id="element-584" />
            </div>
          </div>
        </div>
      </motion.div>

      <ChartSection icon={ZapIcon} iconColor="text-amber-400" title="전기사용량" data={SEL_POWER_DATA[powerRange]} timeRange={powerRange} onTimeRangeChange={setPowerRange} chartType="bar" color="#fbbf24" gradientId="selPower" className="flex-1 min-h-0" stats={[{
      label: '현재',
      value: '12W',
      color: 'text-amber-400'
    }, {
      label: '일평균',
      value: '8.4W'
    }]} data-id="element-585" />

      {/* Combined Temperature + Humidity Card */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="glass-panel rounded-xl p-3 flex-1 min-h-0 flex flex-col" data-id="element-586">
        {/* Header */}
        <div className="flex items-center justify-between mb-2 flex-shrink-0" data-id="element-587">
          <div className="flex items-center gap-2" data-id="element-588">
            <ThermometerIcon className="w-3.5 h-3.5 text-rose-400" data-id="element-589" />
            <DropletsIcon className="w-3 h-3 text-cyan-400 -ml-1" data-id="element-590" />
            <span className="text-[0.9rem] font-bold text-slate-300" data-id="element-591">
              실내온도 · 습도
            </span>
          </div>
          <TimeRangeTabs value={tempHumidityRange} onChange={setTempHumidityRange} data-id="element-592" />
        </div>

        {/* Current Values */}
        <div className="flex items-center gap-3 mb-2 flex-shrink-0" data-id="element-593">
          <div className="flex items-center gap-1.5" data-id="element-594">
            <div className="w-1.5 h-1.5 rounded-full bg-rose-400" data-id="element-595" />
            <span className="text-[0.8rem] text-slate-500 uppercase font-bold" data-id="element-596">
              온도
            </span>
            <span className="text-base font-mono font-bold text-rose-400" data-id="element-597">
              24.5°C
            </span>
          </div>
          <div className="h-3.5 w-px bg-white/10" data-id="element-598" />
          <div className="flex items-center gap-1.5" data-id="element-599">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" data-id="element-600" />
            <span className="text-[0.8rem] text-slate-500 uppercase font-bold" data-id="element-601">
              습도
            </span>
            <span className="text-base font-mono font-bold text-cyan-400" data-id="element-602">
              48%
            </span>
          </div>
        </div>

        {/* Combined Chart */}
        <div className="flex-1 min-h-0 w-full" data-id="element-603">
          <ResponsiveContainer width="100%" height="100%" data-id="element-604">
            <AreaChart data={stableHumidityData} data-id="element-605">
              <defs data-id="element-606">
                <linearGradient id="selTempGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-607">
                  <stop offset="5%" stopColor="#fb7185" stopOpacity={0.25} data-id="element-608" />
                  <stop offset="95%" stopColor="#fb7185" stopOpacity={0} data-id="element-609" />
                </linearGradient>
                <linearGradient id="selHumidGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-610">
                  <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.2} data-id="element-611" />
                  <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} data-id="element-612" />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" hide data-id="element-613" />
              <YAxis hide data-id="element-614" />
              <Tooltip contentStyle={{
              backgroundColor: '#0a0f1a',
              borderColor: '#334155',
              fontSize: '10px',
              borderRadius: '8px'
            }} formatter={(value: number, name: string) => {
              if (name === 'temp') return [`${value}°C`, '온도'];
              return [`${value}%`, '습도'];
            }} data-id="element-615" />
              <Area type="monotone" dataKey="temp" stroke="#fb7185" strokeWidth={2} fillOpacity={1} fill="url(#selTempGrad)" data-id="element-616" />
              <Area type="monotone" dataKey="humidity" stroke="#22d3ee" strokeWidth={1.5} fillOpacity={1} fill="url(#selHumidGrad)" strokeDasharray="4 2" data-id="element-617" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Averages */}
        <div className="flex justify-between mt-1.5 text-xs flex-shrink-0" data-id="element-618">
          <div className="flex items-center gap-1.5" data-id="element-619">
            <div className="w-3 h-0.5 rounded-full bg-rose-400" data-id="element-620" />
            <span className="text-slate-500 text-[0.7rem]" data-id="element-621">평균</span>
            <span className="font-mono text-white" data-id="element-622">23.8°C</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-623">
            <div className="w-3 h-0.5 rounded-full bg-cyan-400 opacity-60" style={{
            borderTop: '1px dashed #22d3ee'
          }} data-id="element-624" />
            <span className="text-slate-500 text-[0.7rem]" data-id="element-625">평균</span>
            <span className="font-mono text-white" data-id="element-626">46.2%</span>
          </div>
        </div>
      </motion.div>

      <ChartSection icon={GaugeIcon} iconColor="text-indigo-400" title="기압" data={SEL_PRESSURE_DATA[pressureRange]} timeRange={pressureRange} onTimeRangeChange={setPressureRange} chartType="line" color="#818cf8" gradientId="selPressure" className="flex-1 min-h-0" stats={[{
      label: '현재',
      value: '1013 hPa',
      color: 'text-indigo-400'
    }, {
      label: '추세',
      value: '↑ 안정'
    }]} data-id="element-627" />
    </div>;
}
// ─── SES Detail View ───
function SESDetail({
  sensor,
  onBack,
  onClose
}: SensorDetailSidebarProps) {
  const [tvocRange, setTvocRange] = useState<TimeRange>('realtime');
  const [co2Range, setCo2Range] = useState<TimeRange>('realtime');
  const [dustRange, setDustRange] = useState<TimeRange>('realtime');
  const [isSwitchOn, setIsSwitchOn] = useState(false);
  const tvocValue = 185;
  const co2Value = 520;
  const dustValue = 22;
  const tvocStatus = tvocValue < 200 ? '좋음' : tvocValue < 400 ? '보통' : '나쁨';
  const tvocColor = tvocValue < 200 ? 'text-emerald-400' : tvocValue < 400 ? 'text-amber-400' : 'text-red-400';
  const co2Status = co2Value < 600 ? '좋음' : co2Value < 1000 ? '보통' : '나쁨';
  const co2Color = co2Value < 600 ? 'text-emerald-400' : co2Value < 1000 ? 'text-amber-400' : 'text-red-400';
  const dustStatus = dustValue < 30 ? '좋음' : dustValue < 80 ? '보통' : '나쁨';
  const dustColor = dustValue < 30 ? 'text-emerald-400' : dustValue < 80 ? 'text-amber-400' : 'text-red-400';
  return <div className="w-full flex flex-col gap-2 p-3 border-l border-white/5 bg-[#0a0f1a]/30 h-full overflow-hidden" data-id="element-628">
      <SensorHeader sensor={sensor} subtitle="Smart Switch" onBack={onBack} onClose={onClose} rightSlot={<ToggleSwitch isOn={isSwitchOn} onToggle={() => setIsSwitchOn(!isSwitchOn)} data-id="element-630" />} data-id="element-629" />

      {/* Quick Status Overview */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="grid grid-cols-3 gap-2 flex-shrink-0" data-id="element-631">
        <div className="glass-panel rounded-xl p-2 text-center" data-id="element-632">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-bold mb-0.5" data-id="element-633">
            TVOC
          </div>
          <div className={`text-base font-mono font-bold ${tvocColor}`} data-id="element-634">
            {tvocValue}
          </div>
          <div className={`text-[0.7rem] font-bold ${tvocColor}`} data-id="element-635">
            {tvocStatus}
          </div>
        </div>
        <div className="glass-panel rounded-xl p-2 text-center" data-id="element-636">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-bold mb-0.5" data-id="element-637">
            CO₂
          </div>
          <div className={`text-base font-mono font-bold ${co2Color}`} data-id="element-638">
            {co2Value}
          </div>
          <div className={`text-[0.7rem] font-bold ${co2Color}`} data-id="element-639">
            {co2Status}
          </div>
        </div>
        <div className="glass-panel rounded-xl p-2 text-center" data-id="element-640">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-bold mb-0.5" data-id="element-641">
            미세먼지
          </div>
          <div className={`text-base font-mono font-bold ${dustColor}`} data-id="element-642">
            {dustValue}
          </div>
          <div className={`text-[0.7rem] font-bold ${dustColor}`} data-id="element-643">
            {dustStatus}
          </div>
        </div>
      </motion.div>

      <ChartSection icon={WindIcon} iconColor="text-emerald-400" title="TVOC" data={SES_TVOC_DATA[tvocRange]} timeRange={tvocRange} onTimeRangeChange={setTvocRange} chartType="area" color="#34d399" gradientId="sesTvoc" className="flex-1 min-h-0" stats={[{
      label: '현재',
      value: `${tvocValue} ppb`,
      color: tvocColor
    }, {
      label: '평균',
      value: '165 ppb'
    }]} data-id="element-644" />

      <ChartSection icon={CloudIcon} iconColor="text-orange-400" title="CO₂" data={SES_CO2_DATA[co2Range]} timeRange={co2Range} onTimeRangeChange={setCo2Range} chartType="area" color="#fb923c" gradientId="sesCo2" className="flex-1 min-h-0" stats={[{
      label: '현재',
      value: `${co2Value} ppm`,
      color: co2Color
    }, {
      label: '평균',
      value: '480 ppm'
    }]} data-id="element-645" />

      <ChartSection icon={ActivityIcon} iconColor="text-pink-400" title="미세먼지 (PM2.5)" data={SES_DUST_DATA[dustRange]} timeRange={dustRange} onTimeRangeChange={setDustRange} chartType="bar" color="#f472b6" gradientId="sesDust" className="flex-1 min-h-0" stats={[{
      label: 'PM2.5',
      value: `${dustValue} µg/m³`,
      color: dustColor
    }, {
      label: 'PM10',
      value: '38 µg/m³'
    }]} data-id="element-646" />
    </div>;
}
// ─── RADAR mock data ───
const RADAR_HR_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 62, 15),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 58, 20),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 60, 18),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 55, 25)
};
const RADAR_BR_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 14, 6),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 12, 8),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 13, 7),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 11, 9)
};
const RADAR_CL_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 85, 15),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 80, 20),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 82, 18),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 78, 22)
};
// ─── RADAR Detail View ───
function RADARDetail({
  sensor,
  onBack,
  onClose
}: SensorDetailSidebarProps) {
  const [hrRange, setHrRange] = useState<TimeRange>('realtime');
  const [brRange, setBrRange] = useState<TimeRange>('realtime');
  const [clRange, setClRange] = useState<TimeRange>('realtime');
  const [isPresent, setIsPresent] = useState(true);
  const hrValue = 72;
  const brValue = 16;
  const clValue = 94;
  const hrStatus = hrValue < 60 ? '서맥' : hrValue > 100 ? '빈맥' : '정상';
  const hrColor = hrValue >= 60 && hrValue <= 100 ? 'text-emerald-400' : hrValue < 60 ? 'text-cyan-400' : 'text-red-400';
  const brStatus = brValue >= 12 && brValue <= 20 ? '정상' : '주의';
  const brColor = brValue >= 12 && brValue <= 20 ? 'text-emerald-400' : 'text-amber-400';
  const clStatus = clValue >= 80 ? '높음' : clValue >= 50 ? '보통' : '낮음';
  const clColor = clValue >= 80 ? 'text-emerald-400' : clValue >= 50 ? 'text-amber-400' : 'text-red-400';
  return <div className="w-full flex flex-col gap-2 p-3 border-l border-white/5 bg-[#0a0f1a]/30 h-full overflow-hidden" data-id="element-647">
      <SensorHeader sensor={sensor} subtitle="Radar Sleep Sensor" onBack={onBack} onClose={onClose} rightSlot={<div className="flex items-center gap-2" data-id="element-649">
            <div className={`flex flex-col items-center gap-1 p-1.5 rounded-xl ${isPresent ? 'bg-rose-500/20 text-rose-400' : 'bg-white/5 text-slate-600'}`} title="재실 여부" data-id="element-650">
              <PersonStandingIcon className="w-7 h-7" fill={isPresent ? 'currentColor' : 'none'} data-id="element-651" />
            </div>
          </div>} data-id="element-648" />

      {/* Quick Status Overview */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="grid grid-cols-3 gap-2 flex-shrink-0" data-id="element-652">
        <div className="glass-panel rounded-xl p-2 text-center" data-id="element-653">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-bold mb-0.5" data-id="element-654">
            HR
          </div>
          <div className={`text-base font-mono font-bold ${hrColor}`} data-id="element-655">
            {hrValue}
          </div>
          <div className="text-[0.6rem] text-slate-600 font-mono" data-id="element-656">bpm</div>
          <div className={`text-[0.7rem] font-bold ${hrColor}`} data-id="element-657">{hrStatus}</div>
        </div>
        <div className="glass-panel rounded-xl p-2 text-center" data-id="element-658">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-bold mb-0.5" data-id="element-659">
            BR
          </div>
          <div className={`text-base font-mono font-bold ${brColor}`} data-id="element-660">
            {brValue}
          </div>
          <div className="text-[0.6rem] text-slate-600 font-mono" data-id="element-661">rpm</div>
          <div className={`text-[0.7rem] font-bold ${brColor}`} data-id="element-662">{brStatus}</div>
        </div>
        <div className="glass-panel rounded-xl p-2 text-center" data-id="element-663">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-bold mb-0.5" data-id="element-664">
            CL
          </div>
          <div className={`text-base font-mono font-bold ${clColor}`} data-id="element-665">
            {clValue}%
          </div>
          <div className="text-[0.6rem] text-slate-600 font-mono" data-id="element-666">신뢰도</div>
          <div className={`text-[0.7rem] font-bold ${clColor}`} data-id="element-667">{clStatus}</div>
        </div>
      </motion.div>

      <ChartSection icon={HeartPulseIcon} iconColor="text-rose-400" title="Heart Rate (HR)" data={RADAR_HR_DATA[hrRange]} timeRange={hrRange} onTimeRangeChange={setHrRange} chartType="area" color="#fb7185" gradientId="radarHr" className="flex-1 min-h-0" stats={[{
      label: '현재',
      value: `${hrValue} bpm`,
      color: hrColor
    }, {
      label: '평균',
      value: '68 bpm'
    }]} data-id="element-668" />

      <ChartSection icon={WindIcon} iconColor="text-sky-400" title="Breathe Rate (BR)" data={RADAR_BR_DATA[brRange]} timeRange={brRange} onTimeRangeChange={setBrRange} chartType="line" color="#38bdf8" gradientId="radarBr" className="flex-1 min-h-0" stats={[{
      label: '현재',
      value: `${brValue} rpm`,
      color: brColor
    }, {
      label: '평균',
      value: '15 rpm'
    }]} data-id="element-669" />

      <ChartSection icon={ShieldCheckIcon} iconColor="text-emerald-400" title="Confidence Level (CL)" data={RADAR_CL_DATA[clRange]} timeRange={clRange} onTimeRangeChange={setClRange} chartType="area" color="#34d399" gradientId="radarCl" className="flex-1 min-h-0" stats={[{
      label: '현재',
      value: `${clValue}%`,
      color: clColor
    }, {
      label: '평균',
      value: '89%'
    }]} data-id="element-670" />
    </div>;
}
// ─── Preset appliance names ───
const PRESET_NAMES = ['냉장고', 'TV', '세탁기', '건조기', '에어드레서'];
// ─── Name Edit Popup ───
function NameEditPopup({
  sensor,
  currentNames,
  primaryName,
  onSave,
  onClose
}: {
  sensor: Sensor;
  currentNames: string[];
  primaryName: string;
  onSave: (names: string[], primary: string) => void;
  onClose: () => void;
}) {
  const [selectedNames, setSelectedNames] = useState<string[]>(currentNames);
  const [primary, setPrimary] = useState<string>(primaryName);
  const [customInput, setCustomInput] = useState('');
  const [customNames, setCustomNames] = useState<string[]>(currentNames.filter(n => !PRESET_NAMES.includes(n) && n !== sensor.label));
  const inputRef = useRef<HTMLInputElement>(null);
  const getSensorIcon = () => {
    switch (sensor.type) {
      case 'SEO':
        return ZapIcon;
      case 'SEL':
        return LightbulbIcon;
      case 'SES':
        return ToggleLeftIcon;
      case 'RADAR':
        return RadarIcon;
      default:
        return ActivityIcon;
    }
  };
  const getSensorColor = () => {
    switch (sensor.type) {
      case 'SEO':
        return 'text-violet-400';
      case 'SEL':
        return 'text-amber-400';
      case 'SES':
        return 'text-cyan-400';
      case 'RADAR':
        return 'text-rose-400';
      default:
        return 'text-white';
    }
  };
  const getSensorBgColor = () => {
    switch (sensor.type) {
      case 'SEO':
        return 'bg-violet-500/15';
      case 'SEL':
        return 'bg-amber-500/15';
      case 'SES':
        return 'bg-cyan-500/15';
      case 'RADAR':
        return 'bg-rose-500/15';
      default:
        return 'bg-white/15';
    }
  };
  const Icon = getSensorIcon();
  const allNames = [...PRESET_NAMES, ...customNames];
  const toggleName = (name: string) => {
    setSelectedNames(prev => {
      const next = prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name];
      // If removing the primary, reset primary
      if (!next.includes(primary)) {
        setPrimary(next[0] || '');
      }
      return next;
    });
  };
  const addCustomName = () => {
    const trimmed = customInput.trim();
    if (!trimmed || allNames.includes(trimmed)) return;
    setCustomNames(prev => [...prev, trimmed]);
    setSelectedNames(prev => [...prev, trimmed]);
    if (!primary) setPrimary(trimmed);
    setCustomInput('');
    inputRef.current?.focus();
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomName();
    }
  };
  const removeCustomName = (name: string) => {
    setCustomNames(prev => prev.filter(n => n !== name));
    setSelectedNames(prev => prev.filter(n => n !== name));
    if (primary === name) {
      const remaining = selectedNames.filter(n => n !== name);
      setPrimary(remaining[0] || '');
    }
  };
  const handleSave = () => {
    if (selectedNames.length === 0) {
      onClose();
      return;
    }
    onSave(selectedNames, primary || selectedNames[0]);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[200] flex items-center justify-center" onClick={onClose} data-id="element-671">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-2xl backdrop-saturate-0 backdrop-brightness-50" data-id="element-672" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} transition={{
      type: 'spring',
      stiffness: 400,
      damping: 30
    }} onClick={e => e.stopPropagation()} className="relative z-10 w-[92%] max-w-md" data-id="element-673">
        <div className="glass-panel rounded-2xl p-5 shadow-2xl border border-white/10" data-id="element-674">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4" data-id="element-675">
            <div className={`p-2.5 rounded-xl ${getSensorBgColor()}`} data-id="element-676">
              <Icon className={`w-5 h-5 ${getSensorColor()}`} data-id="element-677" />
            </div>
            <div className="flex-1" data-id="element-678">
              <div className="text-base font-bold text-white" data-id="element-679">
                센서 이름 설정
              </div>
              <div className="text-[0.8rem] text-slate-500 font-mono" data-id="element-680">
                {sensor.label} · {sensor.type}
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-slate-400 hover:text-white" data-id="element-681">
              <XIcon className="w-4 h-4" data-id="element-682" />
            </button>
          </div>

          {/* Preset Names */}
          <div className="mb-4" data-id="element-683">
            <div className="text-[0.8rem] text-slate-500 uppercase tracking-wider font-bold mb-2 px-0.5" data-id="element-684">
              프리셋 선택
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-685">
              {PRESET_NAMES.map(name => {
              const isSelected = selectedNames.includes(name);
              const isPrimary = primary === name;
              return <button key={name} onClick={() => toggleName(name)} className={`
                      relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all active:scale-95
                      ${isSelected ? 'bg-cyan-500/20 border border-cyan-500/40 text-cyan-300' : 'bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 hover:bg-white/10'}
                    `} data-id="element-686">
                    {isSelected && <CheckIcon className="w-3 h-3" data-id="element-687" />}
                    {name}
                    {isPrimary && isSelected && <StarIcon className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-688" />}
                  </button>;
            })}
            </div>
          </div>

          {/* Custom Names */}
          {customNames.length > 0 && <div className="mb-4" data-id="element-689">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2 px-0.5" data-id="element-690">
                사용자 정의
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-691">
                {customNames.map(name => {
              const isSelected = selectedNames.includes(name);
              const isPrimary = primary === name;
              return <div key={name} className="flex items-center gap-0" data-id="element-692">
                      <button onClick={() => toggleName(name)} className={`
                          flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-l-lg text-xs font-medium transition-all active:scale-95
                          ${isSelected ? 'bg-emerald-500/20 border border-r-0 border-emerald-500/40 text-emerald-300' : 'bg-white/5 border border-r-0 border-white/10 text-slate-400 hover:text-slate-200'}
                        `} data-id="element-693">
                        {isSelected && <CheckIcon className="w-3 h-3" data-id="element-694" />}
                        {name}
                        {isPrimary && isSelected && <StarIcon className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-695" />}
                      </button>
                      <button onClick={() => removeCustomName(name)} className={`
                          p-1.5 rounded-r-lg text-xs transition-all active:scale-95
                          ${isSelected ? 'bg-emerald-500/20 border border-l-0 border-emerald-500/40 text-red-400 hover:text-red-300' : 'bg-white/5 border border-l-0 border-white/10 text-slate-600 hover:text-red-400'}
                        `} data-id="element-696">
                        <XIcon className="w-3 h-3" data-id="element-697" />
                      </button>
                    </div>;
            })}
              </div>
            </div>}

          {/* Custom Input */}
          <div className="mb-4" data-id="element-698">
            <div className="text-[0.8rem] text-slate-500 uppercase tracking-wider font-bold mb-2 px-0.5" data-id="element-699">
              직접 입력
            </div>
            <div className="flex gap-2" data-id="element-700">
              <input ref={inputRef} type="text" value={customInput} onChange={e => setCustomInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="이름 입력..." className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-slate-600 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/30 transition-all" data-id="element-701" />
              <button onClick={addCustomName} disabled={!customInput.trim()} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-sm font-medium hover:bg-cyan-500/30 transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-702">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-703" />
                추가
              </button>
            </div>
          </div>

          {/* Selected Summary & Primary Selection */}
          {selectedNames.length > 0 && <div className="mb-4" data-id="element-704">
              <div className="text-[0.8rem] text-slate-500 uppercase tracking-wider font-bold mb-2 px-0.5 flex items-center gap-1.5" data-id="element-705">
                <StarIcon className="w-3 h-3 text-amber-400" data-id="element-706" />
                대표 이름 지정
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-707">
                {selectedNames.map(name => {
              const isPrimary = primary === name;
              return <button key={name} onClick={() => setPrimary(name)} className={`
                        flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95
                        ${isPrimary ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.15)]' : 'bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10'}
                      `} data-id="element-708">
                      <StarIcon className={`w-3 h-3 ${isPrimary ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} data-id="element-709" />
                      {name}
                    </button>;
            })}
              </div>
            </div>}

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-2 border-t border-white/5" data-id="element-710">
            <button onClick={onClose} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 text-sm font-medium hover:bg-white/10 transition-all active:scale-95" data-id="element-711">
              취소
            </button>
            <button onClick={handleSave} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all active:scale-95 ${selectedNames.length > 0 ? 'bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/30' : 'bg-white/5 border border-white/10 text-slate-500'}`} data-id="element-712">
              <CheckIcon className="w-3.5 h-3.5" data-id="element-713" />
              저장
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
// ─── Reusable Toggle Switch ───
function ToggleSwitch({
  isOn,
  onToggle
}: {
  isOn: boolean;
  onToggle: () => void;
}) {
  return <button onClick={onToggle} className={`relative w-16 h-8 rounded-full transition-colors duration-300 focus:outline-none flex-shrink-0 ${isOn ? 'bg-emerald-500' : 'bg-slate-700'}`} data-id="element-714">
      <div className={`absolute top-1 left-1.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform duration-300 ${isOn ? 'translate-x-7' : 'translate-x-0'}`} data-id="element-715" />
    </button>;
}
// ─── Shared Header ───
function SensorHeader({
  sensor,
  subtitle,
  onBack,
  onClose,
  rightSlot
}: {
  sensor: Sensor;
  subtitle: string;
  onBack: () => void;
  onClose: () => void;
  rightSlot?: React.ReactNode;
}) {
  const [showNameEdit, setShowNameEdit] = useState(false);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [primaryName, setPrimaryName] = useState('');
  const getIcon = () => {
    switch (sensor.type) {
      case 'SEO':
        return ZapIcon;
      case 'SEL':
        return LightbulbIcon;
      case 'SES':
        return ToggleLeftIcon;
      case 'RADAR':
        return RadarIcon;
      default:
        return ActivityIcon;
    }
  };
  const getIconColor = () => {
    switch (sensor.type) {
      case 'SEO':
        return 'text-violet-400 bg-violet-500/15 border-violet-500/30';
      case 'SEL':
        return 'text-amber-400 bg-amber-500/15 border-amber-500/30';
      case 'SES':
        return 'text-cyan-400 bg-cyan-500/15 border-cyan-500/30';
      case 'RADAR':
        return 'text-rose-400 bg-rose-500/15 border-rose-500/30';
      default:
        return 'text-white bg-white/15 border-white/30';
    }
  };
  const Icon = getIcon();
  const displayName = primaryName || sensor.label;
  const handleSaveNames = (names: string[], primary: string) => {
    setSelectedNames(names);
    setPrimaryName(primary);
    setShowNameEdit(false);
  };
  return <>
      <div className="flex items-center gap-2.5 mb-1 flex-shrink-0" data-id="element-716">
        <button onClick={onBack} className="p-1.5 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-white" data-id="element-717">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-718" />
        </button>
        <div className={`p-1.5 rounded-lg border ${getIconColor()}`} data-id="element-719">
          <Icon className="w-4 h-4" data-id="element-720" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-721">
          <div className="text-[0.6rem] font-mono uppercase tracking-widest text-slate-500" data-id="element-722">
            {subtitle}
          </div>
          <div className="flex items-center gap-1.5" data-id="element-723">
            <span className="text-base font-bold text-white truncate" data-id="element-724">
              {displayName}
            </span>
            {selectedNames.length > 1 && <span className="text-[9px] text-slate-500 bg-white/5 px-1.5 py-0.5 rounded-full font-mono" data-id="element-725">
                +{selectedNames.length - 1}
              </span>}
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sensor.active ? 'bg-emerald-400' : 'bg-slate-600'}`} data-id="element-726" />
            <button onClick={() => setShowNameEdit(true)} className="p-1 rounded-md hover:bg-white/10 transition-colors text-slate-500 hover:text-cyan-400 flex-shrink-0" data-id="element-727">
              <PencilIcon className="w-3 h-3" data-id="element-728" />
            </button>
          </div>
        </div>
        {/* Right slot: sensor-specific controls (no settings button) */}
        {rightSlot && <div className="flex-shrink-0 self-center mr-2 mt-1" data-id="element-729">{rightSlot}</div>}
      </div>

      <AnimatePresence data-id="element-730">
        {showNameEdit && <NameEditPopup sensor={sensor} currentNames={selectedNames} primaryName={primaryName} onSave={handleSaveNames} onClose={() => setShowNameEdit(false)} data-id="element-731" />}
      </AnimatePresence>
    </>;
}
// ─── Main Export ───
export function SensorDetailSidebar({
  sensor,
  onBack,
  onClose
}: SensorDetailSidebarProps) {
  const [envTimeRange, setEnvTimeRange] = useState<TimeRange>('realtime');
  const [powerTimeRange, setPowerTimeRange] = useState<TimeRange>('realtime');
  const [mode, setMode] = useState<Mode>('normal');
  const [isOn, setIsOn] = useState(true);
  const [password, setPassword] = useState('1234');
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pendingModeIndex, setPendingModeIndex] = useState<number | null>(null);
  const currentModeIndex = MODES.findIndex(m => m.id === mode);
  // MUST be called before any conditional returns to satisfy Rules of Hooks
  const stableHumidityDataSEO = useMemo(() => {
    return MOCK_DATA[envTimeRange].map((item, i) => ({
      name: item.name,
      temp: item.value,
      humidity: +(38 + Math.sin(i * 0.8) * 12 + i % 3 * 2).toFixed(1)
    }));
  }, [envTimeRange]);
  const navigateMode = (direction: -1 | 1) => {
    const nextIndex = (currentModeIndex + direction + MODES.length) % MODES.length;
    const nextMode = MODES[nextIndex];
    if (mode === 'always' && nextMode.id !== 'always') {
      setPendingModeIndex(nextIndex);
      setShowPasswordVerify(true);
      return;
    }
    setMode(nextMode.id);
  };
  const handlePasswordVerified = (success: boolean) => {
    setShowPasswordVerify(false);
    if (success && pendingModeIndex !== null) {
      setMode(MODES[pendingModeIndex].id);
    }
    setPendingModeIndex(null);
  };
  const handlePasswordChanged = (newPw: string) => {
    setPassword(newPw);
    setShowPasswordChange(false);
  };
  // Route to specific sensor type views
  if (sensor.type === 'SEL') {
    return <SELDetail sensor={sensor} onBack={onBack} onClose={onClose} data-id="element-732" />;
  }
  if (sensor.type === 'SES') {
    return <SESDetail sensor={sensor} onBack={onBack} onClose={onClose} data-id="element-733" />;
  }
  if (sensor.type === 'RADAR') {
    return <RADARDetail sensor={sensor} onBack={onBack} onClose={onClose} data-id="element-734" />;
  }
  // ─── SEO (Outlet) View ───
  return <>
      <div className="w-full flex flex-col gap-2 p-3 border-l border-white/5 bg-[#0a0f1a]/30 h-full overflow-hidden" data-id="element-735">
        <SensorHeader sensor={sensor} subtitle="Smart Edge Outlet" onBack={onBack} onClose={onClose} data-id="element-736" />

        {/* Mode Selection - Spin Box + Toggle */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="glass-panel rounded-xl p-2.5 flex-shrink-0" data-id="element-737">
          <div className="flex items-center gap-2" data-id="element-738">
            {/* Spin Box - half width */}
            <div className="flex items-center w-2/3 bg-white/5 rounded-lg border border-white/10 overflow-hidden" data-id="element-739">
              <button onClick={() => navigateMode(-1)} className="p-2.5 bg-cyan-500/10 hover:bg-cyan-500/25 transition-all text-cyan-400 hover:text-cyan-300 flex-shrink-0 border-r border-white/10 active:scale-90" data-id="element-740">
                <ChevronLeftIcon className="w-4 h-4" data-id="element-741" />
              </button>
              <div className="flex-1 text-center py-1.5 relative overflow-hidden" data-id="element-742">
                <AnimatePresence mode="wait" data-id="element-743">
                  <motion.div key={mode} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.15
                }} className={`text-sm font-bold ${mode === 'always' ? 'text-amber-400' : 'text-cyan-400'}`} data-id="element-744">
                    {MODES[currentModeIndex]?.label}
                  </motion.div>
                </AnimatePresence>
              </div>
              <button onClick={() => navigateMode(1)} className="p-2.5 bg-cyan-500/10 hover:bg-cyan-500/25 transition-all text-cyan-400 hover:text-cyan-300 flex-shrink-0 border-l border-white/10 active:scale-90" data-id="element-745">
                <ChevronRightIcon className="w-4 h-4" data-id="element-746" />
              </button>
            </div>

            {/* Right side: Toggle + Password button */}
            <div className="flex-1 flex items-center justify-end gap-2" data-id="element-747">
              {/* Password Settings Button (only in 'always' mode) */}
              <AnimatePresence data-id="element-748">
                {mode === 'always' && <motion.button initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.8
              }} transition={{
                duration: 0.2
              }} onClick={() => setShowPasswordChange(true)} className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/25 text-amber-400 hover:bg-amber-500/25 transition-all active:scale-95 flex-shrink-0" title="비밀번호 설정" data-id="element-749">
                    <LockIcon className="w-4 h-4" data-id="element-750" />
                  </motion.button>}
              </AnimatePresence>

              {/* Toggle Switch (only in 'normal' mode) */}
              {mode === 'normal' && <ToggleSwitch isOn={isOn} onToggle={() => setIsOn(!isOn)} data-id="element-751" />}
            </div>
          </div>
        </motion.div>

        <ChartSection icon={ZapIcon} iconColor="text-violet-400" title="전기사용량" data={MOCK_DATA[powerTimeRange]} timeRange={powerTimeRange} onTimeRangeChange={setPowerTimeRange} chartType="bar" color="#a78bfa" gradientId="seoPower" className="flex-1 min-h-0" stats={[{
        label: '현재 전력사용량',
        value: '145 W',
        color: 'text-violet-400'
      }]} data-id="element-752" />

        {/* Combined Temperature + Humidity Card */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="glass-panel rounded-xl p-3 flex-1 min-h-0 flex flex-col" data-id="element-753">
          {/* Header */}
          <div className="flex items-center justify-between mb-2 flex-shrink-0" data-id="element-754">
            <div className="flex items-center gap-2" data-id="element-755">
              <ThermometerIcon className="w-3.5 h-3.5 text-rose-400" data-id="element-756" />
              <DropletsIcon className="w-3 h-3 text-cyan-400 -ml-1" data-id="element-757" />
              <span className="text-[0.9rem] font-bold text-slate-300" data-id="element-758">
                실내온도 · 습도
              </span>
            </div>
            <TimeRangeTabs value={envTimeRange} onChange={setEnvTimeRange} data-id="element-759" />
          </div>

          {/* Current Values */}
          <div className="flex items-center gap-3 mb-2 flex-shrink-0" data-id="element-760">
            <div className="flex items-center gap-1.5" data-id="element-761">
              <div className="w-1.5 h-1.5 rounded-full bg-rose-400" data-id="element-762" />
              <span className="text-[0.8rem] text-slate-500 uppercase font-bold" data-id="element-763">
                온도
              </span>
              <span className="text-base font-mono font-bold text-rose-400" data-id="element-764">
                24.5°C
              </span>
            </div>
            <div className="h-3.5 w-px bg-white/10" data-id="element-765" />
            <div className="flex items-center gap-1.5" data-id="element-766">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" data-id="element-767" />
              <span className="text-[0.8rem] text-slate-500 uppercase font-bold" data-id="element-768">
                습도
              </span>
              <span className="text-base font-mono font-bold text-cyan-400" data-id="element-769">
                48%
              </span>
            </div>
          </div>

          {/* Combined Chart */}
          <div className="flex-1 min-h-0 w-full" data-id="element-770">
            <ResponsiveContainer width="100%" height="100%" data-id="element-771">
              <AreaChart data={stableHumidityDataSEO} data-id="element-772">
                <defs data-id="element-773">
                  <linearGradient id="seoTempGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-774">
                    <stop offset="5%" stopColor="#fb7185" stopOpacity={0.25} data-id="element-775" />
                    <stop offset="95%" stopColor="#fb7185" stopOpacity={0} data-id="element-776" />
                  </linearGradient>
                  <linearGradient id="seoHumidGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-777">
                    <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.2} data-id="element-778" />
                    <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} data-id="element-779" />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" hide data-id="element-780" />
                <YAxis hide data-id="element-781" />
                <Tooltip contentStyle={{
                backgroundColor: '#0a0f1a',
                borderColor: '#334155',
                fontSize: '10px',
                borderRadius: '8px'
              }} formatter={(value: number, name: string) => {
                if (name === 'temp') return [`${value}°C`, '온도'];
                return [`${value}%`, '습도'];
              }} data-id="element-782" />
                <Area type="monotone" dataKey="temp" stroke="#fb7185" strokeWidth={2} fillOpacity={1} fill="url(#seoTempGrad)" data-id="element-783" />
                <Area type="monotone" dataKey="humidity" stroke="#22d3ee" strokeWidth={1.5} fillOpacity={1} fill="url(#seoHumidGrad)" strokeDasharray="4 2" data-id="element-784" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Averages */}
          <div className="flex justify-between mt-1.5 text-xs flex-shrink-0" data-id="element-785">
            <div className="flex items-center gap-1.5" data-id="element-786">
              <div className="w-3 h-0.5 rounded-full bg-rose-400" data-id="element-787" />
              <span className="text-slate-500 text-[0.7rem]" data-id="element-788">평균</span>
              <span className="font-mono text-white" data-id="element-789">23.8°C</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-790">
              <div className="w-3 h-0.5 rounded-full bg-cyan-400 opacity-60" style={{
              borderTop: '1px dashed #22d3ee'
            }} data-id="element-791" />
              <span className="text-slate-500 text-[0.7rem]" data-id="element-792">평균</span>
              <span className="font-mono text-white" data-id="element-793">46.2%</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Password Verify Toast */}
      <AnimatePresence data-id="element-794">
        {showPasswordVerify && <PasswordToast type="verify" storedPassword={password} onClose={() => {
        setShowPasswordVerify(false);
        setPendingModeIndex(null);
      }} onVerify={handlePasswordVerified} data-id="element-795" />}
      </AnimatePresence>

      {/* Password Change Toast */}
      <AnimatePresence data-id="element-796">
        {showPasswordChange && <PasswordToast type="change" storedPassword={password} onClose={() => setShowPasswordChange(false)} onChangeComplete={handlePasswordChanged} data-id="element-797" />}
      </AnimatePresence>
    </>;
}